import { onMounted, onBeforeUnmount, effectScope, watch } from 'vue'
import PusherService from '~/libs/pusher-service'
import { useBorrowerStore } from '~/store/borrower'
import { useBankConnectorStore } from '~/store/bank-connector'
import type { Channel } from 'pusher-js'

type AccountStatus = {
  expected: number
  received: number
  failed: number
  finishedAt: string | null
}

type BankSyncStatusPayload = {
  borrowerId: number
  status: string
  accountStatuses: Record<string, AccountStatus>
  aggregate: {
    expected: number
    received: number
    failed: number
  }
}

type PusherEventData = {
  resourceName: string
  object: BankSyncStatusPayload
}

// Module-level (not per-instance) so every caller - the account-list step, the layout chip, and
// any other future mount point - shares one bind on the channel. Pusher's channel.unbind(event)
// drops every listener for that event when called with no specific callback, so a naive
// per-instance bind/unbind would let a transient caller's unmount silently kill the binding for
// every other still-mounted caller (EC-13). Refcounted by the number of mounted callers, not by
// how many of them happen to pass an onStatusUpdated callback.
let boundChannel: Channel | null = null
let sharedHandler: ((data: PusherEventData) => void) | null = null
let activeCallerCount = 0
const activeCallbacks = new Set<() => void>()

// Started once, by whichever caller mounts first, in a detached effect scope so it is not tied
// to - and does not stop when - that particular caller's component unmounts (the same lifetime
// problem the refcounting above exists to solve). middleware/020.pusher.global.js reconnects
// Pusher on a borrower switch, which builds a brand-new Channel object (libs/pusher-service.js's
// connect()) - a persistently-mounted caller's own onMounted/onBeforeUnmount never re-run across
// that switch, so without this watcher boundChannel would keep pointing at the old, now-orphaned
// channel forever and silently stop receiving events for the new borrower (EC-14).
//
// Watches PusherService's channel generation counter, not borrowerId directly: borrowerId is
// written synchronously inside store/borrower.js's setBorrower(), well before the middleware's
// disconnect()/connect() cycle runs on the next navigation. A borrowerId-driven watcher (Vue's
// default 'pre' flush) fires and rebinds to the *old* channel before the middleware ever tears it
// down - the middleware's subsequent disconnect() then wipes out that rebind, and the composable
// never learns about the new channel connect() built, silently killing live updates for the rest
// of the tab session. The channel generation only increments once connect() has actually produced
// a new channel, so this watcher can only ever fire after that channel genuinely exists.
let channelRebindWatcherStarted = false

function ensureChannelRebindWatcher(borrowerStore: ReturnType<typeof useBorrowerStore>) {
  if (channelRebindWatcherStarted) return
  channelRebindWatcherStarted = true

  effectScope(true).run(() => {
    watch(() => PusherService.getChannelGeneration(), (generation, previousGeneration) => {
      if (previousGeneration == null || generation === previousGeneration) return

      if (boundChannel && sharedHandler) {
        boundChannel.unbind('bank-sync-status-updated', sharedHandler)
      }
      boundChannel = null
      sharedHandler = null

      if (activeCallerCount === 0) return

      const channel: Channel = PusherService.getBorrowerChannel()
      if (!channel) {
        console.warn('[useStatementPullStatus] Pusher borrower channel not available')
        return
      }
      sharedHandler = buildSharedHandler(borrowerStore, useBankConnectorStore())
      channel.bind('bank-sync-status-updated', sharedHandler)
      boundChannel = channel
    })
  })
}

function buildSharedHandler(borrowerStore: ReturnType<typeof useBorrowerStore>, bankConnectorStore: ReturnType<typeof useBankConnectorStore>) {
  return async (data: PusherEventData) => {
    if (data?.resourceName !== 'bank-sync-status' || !data?.object) {
      return
    }

    const payload = data.object
    const currentBorrowerId: number | null = borrowerStore.borrowerId

    if (!currentBorrowerId || payload.borrowerId != currentBorrowerId) {
      console.warn('[useStatementPullStatus] Borrower ID mismatch, skipping')
      return
    }

    // Applied once here regardless of how many callers are bound (AC-17), then every caller's
    // own onStatusUpdated is notified.
    await bankConnectorStore.applyStatementPullStatus({
      status: payload.status,
      accountStatuses: payload.accountStatuses,
      aggregate: payload.aggregate,
    })

    activeCallbacks.forEach((callback) => callback())
  }
}

export function useStatementPullStatus(onStatusUpdated?: () => void) {
  const borrowerStore = useBorrowerStore()
  const bankConnectorStore = useBankConnectorStore()

  onMounted(() => {
    ensureChannelRebindWatcher(borrowerStore)

    activeCallerCount += 1
    if (onStatusUpdated) {
      activeCallbacks.add(onStatusUpdated)
    }

    if (!boundChannel) {
      const channel: Channel = PusherService.getBorrowerChannel()
      if (!channel) {
        console.warn('[useStatementPullStatus] Pusher borrower channel not available')
        return
      }
      sharedHandler = buildSharedHandler(borrowerStore, bankConnectorStore)
      channel.bind('bank-sync-status-updated', sharedHandler)
      boundChannel = channel
    }
  })

  onBeforeUnmount(() => {
    activeCallerCount = Math.max(0, activeCallerCount - 1)
    if (onStatusUpdated) {
      activeCallbacks.delete(onStatusUpdated)
    }

    if (activeCallerCount === 0 && boundChannel && sharedHandler) {
      boundChannel.unbind('bank-sync-status-updated', sharedHandler)
      boundChannel = null
      sharedHandler = null
    }
  })
}
